/**
 * Default-collapsed `<details>` block showing the raw `{ sqlErrorCode,
 * message }` and `originalMessage` (if different). Power users / DBAs read
 * this. See Slice 3 → "Underlying error".
 */

import type { DiagnosticsReport } from '../../../types/connectionDiagnostics';

type UnderlyingErrorProps = {
  report: DiagnosticsReport;
  originalMessage?: string;
};

export function DiagnosticsUnderlyingError({ report, originalMessage }: UnderlyingErrorProps) {
  const listing = report.databases.listingError;
  const scope = report.scopeError;
  const hasContent = Boolean(listing || scope);
  if (!hasContent) return null;

  const main = listing ?? scope;
  const sameOriginal = originalMessage && main && originalMessage !== main.message;

  return (
    <details data-testid="diag-underlying-error" className="text-sm group">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground select-none py-1">
        Underlying error (for support)
      </summary>
      <div className="mt-2 space-y-1.5 rounded-md border border-border bg-muted/30 p-3 font-mono text-xs">
        {listing && (
          <Line label="Listing error code" value={fmtCode(listing.sqlErrorCode)} />
        )}
        {listing && <Line label="Listing message" value={listing.message || '—'} />}
        {scope && (
          <Line label="Per-DB error code" value={fmtCode(scope.sqlErrorCode)} />
        )}
        {scope && <Line label="Per-DB message" value={scope.message || '—'} />}
        {sameOriginal && originalMessage && (
          <Line label="Original message" value={originalMessage} />
        )}
      </div>
    </details>
  );
}

function fmtCode(code: number | undefined): string {
  return code !== undefined ? String(code) : '—';
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-2">
      <span className="text-muted-foreground">{label}:</span>
      <span className="break-words">{value}</span>
    </div>
  );
}
